import {
  BuiltRoute,
  CampaignPaymentTab,
  DashboardRoutes,
  FormType,
  IsoLocale,
  OrganizationAssistantDashboardPageParams,
  OrganizationAssistantDestination,
  OrganizationRoutes,
  RouteBuilders,
} from "@simplyk/common";

import { updateOrganizationInformationQueryParam } from "@/features/CreateBankAccountPage/steps/CreateBankAccountStep/helper";

export type DestinationTarget =
  | { kind: "route"; route: BuiltRoute | DashboardRoutes; hash?: string }
  | { kind: "stripe_onboarding" }
  | { kind: "identity_verification" };

interface DestinationContext {
  isoLocale: IsoLocale;
  currentPath: string;
}

type DestinationBuilder = (
  params: OrganizationAssistantDashboardPageParams,
  context: DestinationContext
) => DestinationTarget | null;

const toRoute = (route: BuiltRoute | DashboardRoutes, hash?: string): DestinationTarget => ({
  kind: "route",
  route,
  ...(hash ? { hash } : {}),
});

const DESTINATION_BUILDERS: Record<OrganizationAssistantDestination, DestinationBuilder> = {
  payouts: () => toRoute(OrganizationRoutes.Payouts),
  payout_detail: ({ payoutId }) =>
    toRoute(
      payoutId ? RouteBuilders.buildPayoutTransactionsLink({ isoLocale: null, payoutId }) : OrganizationRoutes.Payouts
    ),
  bank: () => toRoute(OrganizationRoutes.Bank),
  bank_settings: () => toRoute(OrganizationRoutes.Settings),
  create_bank_account: () => toRoute(OrganizationRoutes.CreateBankAccount),
  stripe_onboarding: () => ({ kind: "stripe_onboarding" }),
  identity_verification: () => ({ kind: "identity_verification" }),
  payments: () => toRoute(OrganizationRoutes.Payments),
  payment: ({ transactionId }) =>
    transactionId ? toRoute(new BuiltRoute(OrganizationRoutes.Payments).addSearchParams({ transactionId })) : null,
  form_editor: ({ formId, formType, step }, { isoLocale, currentPath }) => {
    if (!formId) {
      return null;
    }
    const editorLink =
      formType === FormType.Ticketing
        ? RouteBuilders.buildTicketingFormEditLink({ isoLocale, formId, onExitUrl: currentPath })
        : RouteBuilders.buildDonationFormEditLink({ isoLocale, formId, onExitUrl: currentPath });
    return toRoute(editorLink, step);
  },
  campaign_hub: ({ formId, tab }) =>
    formId
      ? toRoute(
          RouteBuilders.buildCampaignHubTabLink({ isoLocale: null, formId, tab: tab ?? CampaignPaymentTab.Overview })
        )
      : null,
  campaigns: () => toRoute(RouteBuilders.buildCampaignListLink({ isoLocale: null })),
  team: () => toRoute(OrganizationRoutes.Settings),
  invite_member: () => toRoute(new BuiltRoute(OrganizationRoutes.Settings).addSearchParams({ addUser: "true" })),
  tax_receipts: () => toRoute(OrganizationRoutes.Receipts),
  organization_settings: () => toRoute(OrganizationRoutes.Settings),
  organization_information: () =>
    toRoute(
      new BuiltRoute(OrganizationRoutes.Settings).addSearchParams({ [updateOrganizationInformationQueryParam]: "true" })
    ),
  organization_page_editor: () => toRoute(RouteBuilders.buildOrganizationPageEditLink({ isoLocale: null })),
  communications: () => toRoute(RouteBuilders.buildEmailDeliveryPageLink()),
  contacts: () => toRoute(OrganizationRoutes.Contacts),
  insights: () => toRoute(RouteBuilders.buildDashboardHomePageUrl()),
};

export const buildDestinationRoute = (
  destination: OrganizationAssistantDestination,
  params: OrganizationAssistantDashboardPageParams,
  context: DestinationContext
): DestinationTarget | null => DESTINATION_BUILDERS[destination](params, context);
